class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.bound === 'true') return; // guard and avoid double-binding
    cartLink.dataset.bound = 'true';

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      // Use the provided selector to extract the correct fragment
      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector || undefined);
      if (sectionElement && html != null) {
        sectionElement.innerHTML = html;
      }
    });

    // Rebind header cart icon events after it was re-rendered
    this.setHeaderCartIconAccessibility();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    // Fallback to full HTML if the expected wrapper is not present
    return node ? node.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble', // important: pull only the bubble fragment
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        // Re-render the header bag bubble on every drawer-driven cart
        // change (remove, qty change) so the count badge stays in sync.
        //
        // Selector `.shopify-section` (NOT `#cart-icon-bubble`) is the
        // key fix: the section HTML returned by Shopify wraps content
        // in `<div class="shopify-section">…</div>`. CartItems'
        // `getSectionInnerHTML` calls `querySelector(selector).innerHTML`
        // with no null-guard — passing `#cart-icon-bubble` made
        // querySelector return null (the ID lives on the OUTER anchor,
        // not inside the section), throwing on `.innerHTML` and aborting
        // the whole update. `.shopify-section` IS present in the section
        // HTML, so the lookup succeeds and the anchor's innerHTML gets
        // replaced with the freshly-rendered bag icon + badge.
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Update the header cart count when quantity changes inside the drawer.
// Runs after CartDrawerItems.updateQuantity() fires PUB_SUB_EVENTS.cartUpdate.
//
// IMPORTANT: handle BOTH header bag variants the theme can render:
//  • Icon variant (bag_display_type == 'icon'): `.bag-count-badge`
//    span sits inside `.bad_cart_icon`, no brackets, hidden when
//    count is 0 — needs to be created/removed dynamically.
//  • Text variant: flat spans with text `[N]` next to the BAG word.
// Previously only the bracket regex was handled, so removing items
// in icon mode left the badge stale (the badge text never updated
// because no span matched the old `/^\s*\[/` pattern).
(function () {
  function updateHeaderCount(event) {
    if (!event || !event.cartData) return;
    var count = event.cartData.item_count;
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    // Icon variant — find or create the .bag-count-badge inside .bad_cart_icon
    var iconWrap = bubble.querySelector('.bad_cart_icon');
    if (iconWrap) {
      var badge = iconWrap.querySelector('.bag-count-badge');
      if (count > 0) {
        var display = count > 99 ? '99+' : String(count);
        if (badge) {
          badge.textContent = display;
        } else {
          var newBadge = document.createElement('span');
          newBadge.className = 'bag-count-badge';
          newBadge.textContent = display;
          iconWrap.appendChild(newBadge);
        }
      } else if (badge) {
        // Count dropped to 0 → remove the badge so the Liquid
        // `{%- if cart.item_count > 0 -%}` semantics are preserved.
        badge.remove();
      }
      return;
    }

    // Text variant — spans with `[N]` text. Keep the legacy update path
    // so themes still using the BAG [n] text variant continue to work.
    var spans = bubble.querySelectorAll('span');
    spans.forEach(function (span) {
      if (/^\s*\[/.test(span.textContent)) {
        span.textContent = '[' + count + ']';
      }
    });
  }

  function bindCartCountUpdate() {
    if (typeof subscribe !== 'function') {
      setTimeout(bindCartCountUpdate, 100);
      return;
    }
    subscribe('cart-update', updateHeaderCount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindCartCountUpdate);
  } else {
    bindCartCountUpdate();
  }
})();
